import { useState } from 'react';
import { AlertTriangle, HeartPulse, RefreshCw, XCircle } from 'lucide-react';
import CapabilityProbes from '../../components/CapabilityProbes';
import { DOCTOR_GRADE_LABELS } from '../../api/doctor';
import type { DoctorReport } from '../../api/doctor';

export interface DoctorPanelProps {
  /** 体检报告；null 表示未加载 / 加载失败。 */
  report: DoctorReport | null;
  /** 体检接口不可用（诚实降级提示）。 */
  unavailable: boolean;
  /** 重新体检回调。 */
  onReload: () => void;
  /** 点击 issue 锚点回调（跳转对应维度 Tab / 运维页面）。 */
  onIssueClick: (anchor: string) => void;
}

/**
 * Doctor 体检面板（P5）：展示健康分、issue 列表与能力探测区块；
 * 接口失败时降级为「不可用」提示，不阻塞页面。
 */
export default function DoctorPanel({ report, unavailable, onReload, onIssueClick }: DoctorPanelProps) {
  const [collapsed, setCollapsed] = useState(false);
  if (!report && !unavailable) return null;
  if (report) {
    return (
      <div className="card mb-4" data-testid="doctor-panel">
        <div
          className="flex items-center justify-between"
          style={{ cursor: 'pointer' }}
          onClick={() => setCollapsed(!collapsed)}
        >
          <div className="flex items-center gap-2">
            <HeartPulse
              size={18}
              style={{
                color:
                  report.grade === 'healthy'
                    ? 'var(--color-success)'
                    : report.grade === 'attention'
                      ? 'var(--color-warning)'
                      : 'var(--color-error)',
              }}
            />
            <strong>体检</strong>
            <span
              className={`badge ${
                report.grade === 'healthy'
                  ? 'success'
                  : report.grade === 'attention'
                    ? 'warning'
                    : 'error'
              }`}
            >
              {report.score} 分 · {DOCTOR_GRADE_LABELS[report.grade]}
            </span>
            {report.issues.length > 0 && (
              <span className="text-muted" style={{ fontSize: 13 }}>
                {report.issues.length} 个待处理项
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="secondary"
              title="重新体检"
              onClick={(e) => {
                e.stopPropagation();
                onReload();
              }}
            >
              <RefreshCw size={14} />
            </button>
            <span className="text-muted" style={{ fontSize: 13 }}>
              {collapsed ? '展开' : '收起'}
            </span>
          </div>
        </div>
        {!collapsed && (
          <div style={{ marginTop: 12 }}>
            {report.issues.length === 0 ? (
              <p className="text-muted" style={{ margin: 0 }}>
                一切正常，没有发现问题。
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {report.issues.map((issue) => (
                  <button
                    key={issue.id}
                    className="text-left"
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      gap: 10,
                      alignItems: 'flex-start',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid var(--border-default)',
                      background: 'transparent',
                      cursor: 'pointer',
                      color: 'inherit',
                    }}
                    title="点击跳转到对应配置维度"
                    onClick={() => onIssueClick(issue.anchor)}
                  >
                    {issue.severity === 'error' ? (
                      <XCircle size={16} style={{ color: 'var(--color-error)', flexShrink: 0, marginTop: 2 }} />
                    ) : (
                      <AlertTriangle size={16} style={{ color: 'var(--color-warning)', flexShrink: 0, marginTop: 2 }} />
                    )}
                    <span style={{ flex: 1 }}>
                      <span style={{ display: 'block', fontWeight: 600, fontSize: 14 }}>
                        {issue.title}
                        <span className="text-muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
                          -{issue.deduction} 分
                        </span>
                      </span>
                      <span className="text-muted" style={{ display: 'block', fontSize: 13, marginTop: 2 }}>
                        {issue.description}
                      </span>
                      <span style={{ display: 'block', fontSize: 13, marginTop: 4, color: 'var(--color-accent, #58A6FF)' }}>
                        建议：{issue.suggestion}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            {/* 能力探测区块（B7）：旧后端无 probes 字段时不渲染 */}
            <CapabilityProbes probes={report.probes} />
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="card mb-4">
      <span className="text-muted" style={{ fontSize: 13 }}>
        <HeartPulse size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
        体检服务暂不可用
      </span>
    </div>
  );
}
